'use client'

import { FluxoCaixaReport, getFluxoCaixaReport, exportFluxoCaixa } from '@/lib/api/financial/financial-accounting-api'
import { useEffect, useState } from 'react'
import { MdArrowBack, MdPrint, MdFileDownload } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

const formatCurrency = (cents: number) =>
    (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const getDefaultDates = () => {
    const now = new Date()
    return {
        startDate: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10),
        endDate: new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10),
    }
}

const formatPeriodLabel = (period: string, groupBy: string) => {
    if (groupBy === 'month') {
        const [y, m] = period.split('-')
        return `${m}/${y}`
    }
    return new Date(period + 'T00:00:00').toLocaleDateString('pt-BR')
}

export default function FluxoCaixaReportPage() {
    const navigate = useNavigate()
    const [data, setData] = useState<FluxoCaixaReport | null>(null)
    const [loading, setLoading] = useState(true)
    const [startDate, setStartDate] = useState(getDefaultDates().startDate)
    const [endDate, setEndDate] = useState(getDefaultDates().endDate)
    const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month'>('day')

    useEffect(() => { loadData() }, [startDate, endDate, groupBy])

    const loadData = async () => {
        setLoading(true)
        try {
            const res = await getFluxoCaixaReport(startDate, endDate, groupBy)
            if (res.success && res.result) setData(res.result)
        } catch { toast.error('Erro ao carregar fluxo de caixa') }
        finally { setLoading(false) }
    }

    const maxValue = data ? Math.max(...data.data.map(d => Math.max(d.income, d.expense)), 1) : 1

    return (
        <div className="p-4 md:p-6 max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}><MdArrowBack size={20} /></button>
                    <h1 className="text-2xl font-bold">Fluxo de Caixa</h1>
                </div>
                <div className="flex items-center gap-1">
                    <div className="dropdown dropdown-end">
                        <label tabIndex={0} className="btn btn-sm btn-outline gap-1">
                            <MdFileDownload size={16} />
                            Exportar
                        </label>
                        <ul tabIndex={0} className="dropdown-content menu bg-base-100 rounded-box z-50 w-44 p-2 shadow-lg border border-base-300">
                            <li><button onClick={() => { exportFluxoCaixa(startDate, endDate, 'xlsx', groupBy).catch(() => toast.error('Erro ao exportar')) }}>Excel (.xlsx)</button></li>
                            <li><button onClick={() => { exportFluxoCaixa(startDate, endDate, 'pdf', groupBy).catch(() => toast.error('Erro ao exportar')) }}>PDF</button></li>
                        </ul>
                    </div>
                    <button className="btn btn-ghost btn-sm" onClick={() => window.print()}><MdPrint size={18} /></button>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-4">
                <input type="date" className="input input-bordered input-sm" value={startDate} onChange={e => setStartDate(e.target.value)} />
                <input type="date" className="input input-bordered input-sm" value={endDate} onChange={e => setEndDate(e.target.value)} />
                <select className="select select-bordered select-sm" value={groupBy} onChange={e => setGroupBy(e.target.value as any)}>
                    <option value="day">Diário</option>
                    <option value="week">Semanal</option>
                    <option value="month">Mensal</option>
                </select>
            </div>

            {loading && <div className="flex justify-center py-20"><div className="loading loading-spinner loading-lg text-secondary"></div></div>}

            {!loading && data && (
                <div className="flex flex-col gap-6">
                    {/* Resumo */}
                    <div className="grid grid-cols-3 gap-4">
                        <div className="card bg-base-100 shadow-sm border border-base-300/50">
                            <div className="card-body p-4">
                                <span className="text-sm text-base-content/60">Entradas</span>
                                <p className="text-xl font-bold text-success">{formatCurrency(data.totalIncome)}</p>
                            </div>
                        </div>
                        <div className="card bg-base-100 shadow-sm border border-base-300/50">
                            <div className="card-body p-4">
                                <span className="text-sm text-base-content/60">Saídas</span>
                                <p className="text-xl font-bold text-error">{formatCurrency(data.totalExpense)}</p>
                            </div>
                        </div>
                        <div className="card bg-base-100 shadow-sm border border-base-300/50">
                            <div className="card-body p-4">
                                <span className="text-sm text-base-content/60">Saldo</span>
                                <p className={`text-xl font-bold ${data.netCashFlow >= 0 ? 'text-success' : 'text-error'}`}>
                                    {formatCurrency(data.netCashFlow)}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Gráfico de barras simples */}
                    {data.data.length > 0 && (
                        <div className="card bg-base-100 shadow-sm border border-base-300/50">
                            <div className="card-body p-4">
                                <h3 className="font-semibold text-sm mb-4">Entradas x Saídas por período</h3>
                                <div className="space-y-3">
                                    {data.data.map((d, i) => (
                                        <div key={i}>
                                            <div className="flex items-center justify-between text-xs mb-1">
                                                <span className="font-mono">{formatPeriodLabel(d.period, groupBy)}</span>
                                                <span className={`font-semibold ${d.income - d.expense >= 0 ? 'text-success' : 'text-error'}`}>
                                                    {formatCurrency(d.income - d.expense)}
                                                </span>
                                            </div>
                                            <div className="flex gap-1">
                                                <div
                                                    className="h-4 bg-success/70 rounded-sm transition-all"
                                                    style={{ width: `${(d.income / maxValue) * 100}%`, minWidth: d.income > 0 ? '4px' : '0' }}
                                                    title={`Entrada: ${formatCurrency(d.income)}`}
                                                />
                                                <div
                                                    className="h-4 bg-error/70 rounded-sm transition-all"
                                                    style={{ width: `${(d.expense / maxValue) * 100}%`, minWidth: d.expense > 0 ? '4px' : '0' }}
                                                    title={`Saída: ${formatCurrency(d.expense)}`}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex items-center gap-4 mt-3 text-xs text-base-content/60">
                                    <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm bg-success/70"></div> Entradas</div>
                                    <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm bg-error/70"></div> Saídas</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Tabela detalhada */}
                    <div className="card bg-base-100 shadow print:shadow-none overflow-x-auto">
                        <table className="table table-sm">
                            <thead>
                                <tr>
                                    <th>Período</th>
                                    <th className="text-right">Entradas</th>
                                    <th className="text-right">Saídas</th>
                                    <th className="text-right">Saldo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.data.map((d, i) => {
                                    const net = d.income - d.expense
                                    return (
                                        <tr key={i}>
                                            <td className="font-mono">{formatPeriodLabel(d.period, groupBy)}</td>
                                            <td className="text-right font-mono text-success">{d.income > 0 ? formatCurrency(d.income) : '-'}</td>
                                            <td className="text-right font-mono text-error">{d.expense > 0 ? formatCurrency(d.expense) : '-'}</td>
                                            <td className={`text-right font-mono font-semibold ${net >= 0 ? 'text-success' : 'text-error'}`}>
                                                {formatCurrency(net)}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    )
}
